import { useMemo } from 'react';
import {
  GridRenderCellParams,
  Box,
  Avatar,
  Stack,
  Typography,
  Button,
  MdEdit,
  GridColDef,
  GridRowsProp,
  ControllerDataGrid,
} from '@bymason-private/legacy-pattern-components';
import { UserModel } from '@bymason/controller-api-hooks';
import { formatLongDateTimeString } from '@bymason/utils';

interface UserRenderProps {
  users: UserModel[];
  handleEditUserModalOpen: (user: UserModel) => void;
}

export function UserList({ users, handleEditUserModalOpen }: UserRenderProps) {
  const RenderEditButton = (params: GridRenderCellParams) => {
    const user = params.row.user as UserModel;
    const handleButtonClick = () => handleEditUserModalOpen(user);
    return (
      <Box sx={{ display: 'flex' }}>
        <Button startIcon={<MdEdit size='12' />} onClick={handleButtonClick}>
          Edit{' '}
        </Button>
      </Box>
    );
  };

  const gridColumns: GridColDef[] = [
    {
      field: 'user',
      headerName: 'User',
      flex: 2,
      renderCell: UserRender,
      minWidth: 300,
      sortComparator: (a: UserModel, b: UserModel) => {
        return a.email > b.email ? 1 : -1;
      },
    },
    {
      field: 'lastLogin',
      headerName: 'Last login',
      type: 'date',
      maxWidth: 300,
      flex: 1,
      valueFormatter: (params) => {
        return params.value ? formatLongDateTimeString(params.value as Date) : '-';
      },
    },
    {
      field: 'scope',
      headerName: 'Scopes',
      type: 'string',
      flex: 1,
    },
    {
      field: 'action',
      headerName: 'Action',
      renderCell: RenderEditButton,
      minWidth: 120,
      sortable: false,
      align: 'right',
    },
  ];

  const gridRows: GridRowsProp = useMemo(
    () =>
      users
        ? users.map((user) => ({
            user: user,
            lastLogin: user.last_login,
            scope: user.scopes?.join(', '),
            action: user,
            id: user.user_id,
          }))
        : [],
    [users]
  );

  return (
    <ControllerDataGrid
      sx={{ mt: 2 }}
      columns={gridColumns}
      disableSelectionOnClick
      disableColumnResize={false}
      loading={!users}
      rows={gridRows}
      rowHeight={70}
    />
  );
}

const UserRender = (params: GridRenderCellParams) => {
  const user = params.row.user as UserModel;
  return (
    <Box my={1} display='flex' alignItems='center'>
      <Avatar src={user.picture} sx={{ mr: 2, my: 0.5 }} />
      <Stack>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Typography>{user.name}</Typography>
        </Box>
        <Typography fontWeight={700}>{user.email}</Typography>
      </Stack>
    </Box>
  );
};
